import { useMemo, useCallback } from 'react';
import styles from './DocItemsTable.module.css';
import { cx } from '../../lib/cx';

export interface DocItem {
  id: string;
  desc: string;
  qty: string;
  unit: string;
  price: string;
  total: string;
}

export interface DocItemsTableProps {
  items: DocItem[];
  onChange: (items: DocItem[]) => void;
  /** Titulek sekce, např. „Položky rozpočtu". */
  title?: React.ReactNode;
  /** Popisek tlačítka + přidat. */
  addLabel?: string;
}

function parseNum(v: string): number {
  const cleaned = (v ?? '').replace(/\s/g, '').replace(',', '.').replace(/[^\d.-]/g, '');
  const n = Number(cleaned);
  return isFinite(n) ? n : 0;
}

function formatNum(n: number): string {
  if (!isFinite(n) || n === 0) return '';
  return n.toLocaleString('cs-CZ').replace(/,/g, ' ');
}

export function DocItemsTable({ items, onChange, title = 'Položky', addLabel = '+ přidat položku' }: DocItemsTableProps) {
  const subtotal = useMemo(
    () => items.reduce((acc, it) => acc + parseNum(it.total), 0),
    [items],
  );

  const update = useCallback(
    (id: string, patch: Partial<DocItem>) => {
      onChange(
        items.map((r) => {
          if (r.id !== id) return r;
          const merged = { ...r, ...patch };
          if ('qty' in patch || 'price' in patch) {
            const q = parseNum(merged.qty);
            const p = parseNum(merged.price);
            if (q && p) merged.total = formatNum(Math.round(q * p));
          }
          return merged;
        }),
      );
    },
    [items, onChange],
  );

  const add = useCallback(() => {
    const id =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `it-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    onChange([...items, { id, desc: '', qty: '', unit: '', price: '', total: '' }]);
  }, [items, onChange]);

  const remove = useCallback(
    (id: string) => {
      onChange(items.filter((r) => r.id !== id));
    },
    [items, onChange],
  );

  return (
    <section className={styles.section}>
      <div className={styles.head}>
        <div className={styles.title}>{title}</div>
        <button type="button" className={styles.addBtn} onClick={add}>
          {addLabel}
        </button>
      </div>
      <div className={styles.table}>
        <div className={styles.row + ' ' + styles.rowHead}>
          <div>POPIS</div>
          <div className={styles.ar}>MNOŽ.</div>
          <div>MJ</div>
          <div className={styles.ar}>CENA / MJ</div>
          <div className={styles.ar}>CELKEM</div>
          <div />
        </div>
        {items.map((it) => (
          <div key={it.id} className={styles.row}>
            <label className={cx(styles.cell, styles.cellDesc)}>
              <span className={styles.cellLabel}>Popis</span>
              <input
                className={styles.input}
                value={it.desc}
                placeholder="Popis položky"
                onChange={(e) => update(it.id, { desc: e.target.value })}
              />
            </label>
            <label className={styles.cell}>
              <span className={styles.cellLabel}>Množ.</span>
              <input
                className={cx(styles.input, styles.ar, styles.mono)}
                value={it.qty}
                placeholder="0"
                onChange={(e) => update(it.id, { qty: e.target.value })}
              />
            </label>
            <label className={styles.cell}>
              <span className={styles.cellLabel}>MJ</span>
              <input
                className={cx(styles.input, styles.mono)}
                value={it.unit}
                placeholder="m³"
                onChange={(e) => update(it.id, { unit: e.target.value })}
              />
            </label>
            <label className={styles.cell}>
              <span className={styles.cellLabel}>Cena / MJ</span>
              <input
                className={cx(styles.input, styles.ar, styles.mono)}
                value={it.price}
                placeholder="0"
                onChange={(e) => update(it.id, { price: e.target.value })}
              />
            </label>
            <label className={styles.cell}>
              <span className={styles.cellLabel}>Celkem</span>
              <input
                className={cx(styles.input, styles.ar, styles.mono, styles.bold)}
                value={it.total}
                placeholder="0"
                onChange={(e) => update(it.id, { total: e.target.value })}
              />
            </label>
            <button
              type="button"
              className={styles.del}
              onClick={() => remove(it.id)}
              aria-label="Smazat řádek"
              title="Smazat řádek"
            >
              ✕
            </button>
          </div>
        ))}
        {items.length === 0 && (
          <div className={cx(styles.empty, 'docNoPrint')}>Žádné položky. Klikněte „{addLabel}".</div>
        )}
        {items.length > 0 && (
          <div className={cx(styles.row, styles.rowTotal)}>
            <div />
            <div />
            <div />
            <div className={cx(styles.ar, styles.mono, styles.subdued)}>Mezisoučet</div>
            <div className={cx(styles.ar, styles.mono, styles.bold)}>{formatNum(subtotal)} Kč</div>
            <div />
          </div>
        )}
      </div>
    </section>
  );
}
